import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const termsSections = [
  {
    title: "1. Agreement to the Terms",
    paragraphs: [
      "By creating an account, accessing Josh, or using any Josh feature, you agree to these Terms. If you do not agree, do not use Josh. If you use Josh on behalf of an organization, you represent that you have authority to bind that organization to these Terms.",
    ],
  },
  {
    title: "2. What Josh is",
    paragraphs: [
      "Josh is a personal assistant service that helps users organize and execute real-world tasks such as bill tracking, reminders, expense monitoring, shopping research, travel preparation, and partner app handoffs. Josh may use automation, workflow logic, and AI-assisted reasoning to help you complete requests, but Josh is not a law firm, accounting firm, financial adviser, travel agent of record, insurer, bank, or emergency service.",
    ],
  },
  {
    title: "3. Eligibility and accounts",
    bullets: [
      "You must be legally capable of entering into a binding agreement in your location.",
      "You must provide accurate registration information and keep it current.",
      "You are responsible for maintaining the confidentiality of your account credentials and for activity under your account.",
      "You may not impersonate another person, create accounts by automated means without authorization, or attempt to bypass Josh security or rate limits.",
    ],
  },
  {
    title: "4. Connected accounts and permissions",
    paragraphs: [
      "Some Josh features require you to connect third-party services such as Gmail, Outlook, Stripe, messaging services, travel providers, or shopping partners. When you connect those services, you authorize Josh to access and process the data that is reasonably necessary to provide the feature you selected. You remain responsible for your relationship with the connected provider and for complying with that provider’s own terms.",
    ],
  },
  {
    title: "5. User instructions and handoffs",
    paragraphs: [
      "Josh may prepare an action, summarize results, or hand you off to a third-party app or merchant to complete a purchase, payment, booking, or confirmation. When Josh opens a deep link, checkout session, booking page, or provider handoff, the final transaction is usually completed under the third party’s own terms and policies, not Josh’s.",
    ],
    bullets: [
      "You are responsible for reviewing final prices, taxes, fees, cancellation terms, booking details, and merchant conditions before you confirm a third-party transaction.",
      "Josh is not responsible for errors introduced by third-party providers, inventory changes, provider outages, or price changes that occur after a search or preview response.",
    ],
  },
  {
    title: "6. Inbox, bills, and expense features",
    paragraphs: [
      "If you connect an email account, Josh may scan relevant messages for bills, subscriptions, receipts, recurring purchases, expense events, and related workflow signals. These features are designed to help you monitor spending and surface reminders, but they are informational tools only. You remain responsible for reviewing your own statements, invoices, and payment obligations.",
    ],
  },
  {
    title: "7. Acceptable use",
    bullets: [
      "Do not use Josh for illegal activity, fraud, harassment, stalking, abuse, spam, or unauthorized surveillance.",
      "Do not attempt to reverse engineer, scrape, overload, disrupt, or interfere with Josh infrastructure or provider integrations.",
      "Do not use Josh to access third-party data or accounts without proper authorization.",
      "Do not submit content or instructions that infringe intellectual property, privacy, publicity, or other rights.",
    ],
  },
  {
    title: "8. AI outputs and operational limits",
    paragraphs: [
      "Josh may generate summaries, classifications, suggestions, rankings, expense insights, or conversational responses. These outputs may be incomplete or incorrect. You should independently review important information, especially before making legal, medical, tax, financial, payment, or travel decisions. Josh may refuse, pause, or ask for clarification where a request is unsafe, unsupported, or requires confirmation.",
    ],
  },
  {
    title: "9. Fees, payments, and subscriptions",
    paragraphs: [
      "Paid plans, payment methods, spending controls, and provider billing features may be offered through Josh. If you subscribe to a paid plan or authorize a payment workflow, you agree to pay the disclosed charges and any applicable taxes or fees. Additional partner charges may apply where a third-party service is involved.",
    ],
    bullets: [
      "Unless local law requires otherwise, subscriptions may renew until cancelled.",
      "Partner payments, utility payments, travel bookings, and merchant purchases may have separate refund, cancellation, or chargeback rules.",
    ],
  },
  {
    title: "10. Intellectual property",
    paragraphs: [
      "Josh and its service design, software, workflows, branding, and non-user content are protected by intellectual property laws. Subject to these Terms, Josh grants you a limited, revocable, non-exclusive, non-transferable right to use the service for its intended purpose. You retain rights in content you submit, but you grant Josh the rights needed to host, process, and use that content to operate the service.",
    ],
  },
  {
    title: "11. Privacy",
    paragraphs: [
      "Hey Josh’s handling of personal information is described in the Hey Josh Privacy Policy. By using Hey Josh, you acknowledge that Hey Josh may process your information as described there.",
    ],
  },
  {
    title: "12. Suspension and termination",
    paragraphs: [
      "Josh may suspend or terminate access to the service if you breach these Terms, create legal risk, misuse the service, threaten security, or use the service in a way that could harm Josh, its users, or third-party providers. You may stop using Josh at any time. Certain provisions of these Terms, including those relating to payments already incurred, disputes, intellectual property, privacy, and limitations of liability, will survive termination to the extent permitted by law.",
    ],
  },
  {
    title: "13. Disclaimers",
    paragraphs: [
      "To the maximum extent permitted by law, Josh is provided on an “as is” and “as available” basis. Josh does not guarantee uninterrupted availability, provider uptime, complete accuracy, or that every request will be completed successfully. Third-party integrations may change, become unavailable, or impose additional conditions outside Josh’s control.",
      "Nothing in these Terms excludes rights that cannot lawfully be excluded, including mandatory consumer guarantees or statutory protections that apply in your country or state.",
    ],
  },
  {
    title: "14. Limitation of liability",
    paragraphs: [
      "To the maximum extent permitted by law, Josh and its operators will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, revenues, goodwill, data, or business opportunities arising from your use of Josh or any third-party service accessed through Josh. Josh’s total liability for claims arising out of or related to the service will be limited to the amount you paid Josh for the service giving rise to the claim during the 12 months before the event, or, if greater and required by law, the minimum amount mandated by applicable law.",
    ],
  },
  {
    title: "15. Regional consumer protections",
    paragraphs: [
      "If you live in a jurisdiction with mandatory consumer rights, privacy rights, or digital platform protections, those rights continue to apply and will prevail over conflicting language in these Terms to the extent required by law. Josh intends these Terms to be interpreted consistently with applicable consumer law in the United States, Canada, the European Economic Area, the United Kingdom, Australia, New Zealand, and other places where Josh is made available.",
    ],
  },
  {
    title: "16. Changes to Josh or these Terms",
    paragraphs: [
      "Josh may modify features, providers, integrations, pricing, or these Terms from time to time. If a change is material, Josh may provide notice in the app, on the website, or by email before the change takes effect where required by law. Your continued use after the effective date of the updated Terms means you accept the revised Terms.",
    ],
  },
  {
    title: "17. Governing law and disputes",
    paragraphs: [
      "Unless mandatory local law requires otherwise, disputes relating to Josh will be governed by the law of the jurisdiction identified in the service’s operating entity or applicable service notice. If local law gives you the right to bring claims in your home courts or under your home consumer laws, nothing in these Terms is intended to take away those rights.",
    ],
  },
  {
    title: "18. Contact",
    paragraphs: [
      "Questions about these Terms can be sent to dpenhaaaron@gmail.com.",
    ],
  },
];

const JoshTermsPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  };

  return (
    <div className="min-h-screen pt-20" data-testid="josh-terms-page">
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <Link to="/joshai" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Hey Josh
            </Link>

            <p className="caption mb-4">Terms & Conditions</p>
            <h1 className="heading-2 mb-6">Hey Josh</h1>
            <p className="body-text mb-10">
              Last updated March 16, 2026. These Terms govern your use of Josh’s mobile
              app, web app, APIs, connected workflows, and related services.
            </p>

            <div className="space-y-6">
              {termsSections.map((section) => (
                <div key={section.title} className="feature-card">
                  <h2 className="heading-3 mb-4">{section.title}</h2>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph} className="body-text mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                  {section.bullets && (
                    <ul className="space-y-3 text-base leading-relaxed text-zinc-600">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JoshTermsPage;
